import React, { useState } from 'react';
import { BusinessIdea, RevenueModel, MarketAnalysis, ResourceRequirements, LaunchGuide } from '../../types/ai';
import { useLanguage } from '../../context/LanguageContext';
import './BusinessKitEnhancements.css';

interface BusinessKitEnhancementsProps {
  idea: BusinessIdea;
}

export const BusinessKitEnhancements: React.FC<BusinessKitEnhancementsProps> = ({ idea }) => {
  const { t } = useLanguage();
  const [expandedSection, setExpandedSection] = useState<string | null>('revenue');

  const toggleSection = (section: string) => {
    setExpandedSection(prev => prev === section ? null : section);
  };

  // Check if idea has business kit enhancements
  const hasEnhancements = idea.revenueModel || idea.marketAnalysis || idea.resourceRequirements || idea.launchGuide;

  if (!hasEnhancements) {
    return null;
  }

  return (
    <div className="business-kit-enhancements">
      <div className="enhancements-header">
        <h3>{t('businessKit.title')}</h3>
        <p>{t('businessKit.subtitle')}</p>
      </div>

      {/* Revenue Model Section */}
      {idea.revenueModel && (
        <RevenueModelSection
          revenueModel={typeof idea.revenueModel === 'string' ? null : idea.revenueModel}
          isExpanded={expandedSection === 'revenue'}
          onToggle={() => toggleSection('revenue')}
        />
      )}

      {/* Market Analysis Section */}
      {idea.marketAnalysis && (
        <MarketAnalysisSection
          marketAnalysis={idea.marketAnalysis}
          isExpanded={expandedSection === 'market'}
          onToggle={() => toggleSection('market')}
        />
      )}

      {/* Resource Requirements Section */}
      {idea.resourceRequirements && (
        <ResourceRequirementsSection
          resourceRequirements={idea.resourceRequirements}
          isExpanded={expandedSection === 'resources'}
          onToggle={() => toggleSection('resources')}
        />
      )}

      {/* Launch Guide Section */}
      {idea.launchGuide && (
        <LaunchGuideSection
          launchGuide={idea.launchGuide}
          isExpanded={expandedSection === 'launch'}
          onToggle={() => toggleSection('launch')}
        />
      )}
    </div>
  );
};

// Revenue Model Component
interface RevenueModelSectionProps {
  revenueModel: RevenueModel | null;
  isExpanded: boolean;
  onToggle: () => void;
}

const RevenueModelSection: React.FC<RevenueModelSectionProps> = ({ revenueModel, isExpanded, onToggle }) => {
  const { t } = useLanguage();

  if (!revenueModel) return null;

  return (
    <div className="enhancement-section">
      <button className="section-header" onClick={onToggle} type="button">
        <div className="section-title-group">
          <span className="section-icon">💰</span>
          <h4>{t('businessKit.revenueModel.title')}</h4>
        </div>
        <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`} />
      </button>

      {isExpanded && (
        <div className="section-content">
          {revenueModel.estimatedRevenue && (
            <div className="revenue-estimate">
              <span className="estimate-label">{t('businessKit.revenueModel.estimatedRevenue')}</span>
              <span className="estimate-value">{revenueModel.estimatedRevenue}</span>
            </div>
          )}

          <div className="monetization-strategies">
            {revenueModel.strategies.map((strategy, index) => (
              <div key={index} className="strategy-card">
                <div className="strategy-header">
                  <span className={`strategy-type ${strategy.type.toLowerCase().replace(/\s+/g, '-')}`}>
                    {strategy.type}
                  </span>
                </div>
                <p className="strategy-description">{strategy.description}</p>
                <div className="strategy-details">
                  <div className="detail-item">
                    <span className="detail-label">{t('businessKit.revenueModel.pricing')}</span>
                    <span className="detail-value">{strategy.pricingExample}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('businessKit.revenueModel.targetSegment')}</span>
                    <span className="detail-value">{strategy.targetSegment}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Market Analysis Component
interface MarketAnalysisSectionProps {
  marketAnalysis: MarketAnalysis;
  isExpanded: boolean;
  onToggle: () => void;
}

const MarketAnalysisSection: React.FC<MarketAnalysisSectionProps> = ({ marketAnalysis, isExpanded, onToggle }) => {
  const { t } = useLanguage();

  return (
    <div className="enhancement-section">
      <button className="section-header" onClick={onToggle} type="button">
        <div className="section-title-group">
          <span className="section-icon">📊</span>
          <h4>{t('businessKit.marketAnalysis.title')}</h4>
        </div>
        <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`} />
      </button>

      {isExpanded && (
        <div className="section-content">
          <div className="market-size-card">
            <h5>{t('businessKit.marketAnalysis.marketSize')}</h5>
            <p className="market-size-value">{marketAnalysis.marketSize}</p>
          </div>

          {marketAnalysis.competitors && marketAnalysis.competitors.length > 0 && (
            <div className="competitors-section">
              <h5>{t('businessKit.marketAnalysis.competitors')}</h5>
              <div className="competitors-grid">
                {marketAnalysis.competitors.map((competitor, index) => (
                  <div key={index} className="competitor-card">
                    <h6 className="competitor-name">{competitor.name}</h6>
                    <div className="competitor-analysis">
                      <div className="strengths">
                        <span className="analysis-label">
                          <i className="bi bi-check-circle" />
                          {t('businessKit.marketAnalysis.strengths')}
                        </span>
                        <ul>
                          {competitor.strengths.map((strength, i) => (
                            <li key={i}>{strength}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="weaknesses">
                        <span className="analysis-label">
                          <i className="bi bi-x-circle" />
                          {t('businessKit.marketAnalysis.weaknesses')}
                        </span>
                        <ul>
                          {competitor.weaknesses.map((weakness, i) => (
                            <li key={i}>{weakness}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {marketAnalysis.opportunityGaps && marketAnalysis.opportunityGaps.length > 0 && (
            <div className="opportunities-section">
              <h5>{t('businessKit.marketAnalysis.opportunities')}</h5>
              <div className="opportunities-list">
                {marketAnalysis.opportunityGaps.map((gap, index) => (
                  <div key={index} className="opportunity-item">
                    <i className="bi bi-lightbulb" />
                    <span>{gap}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Resource Requirements Component
interface ResourceRequirementsSectionProps {
  resourceRequirements: ResourceRequirements;
  isExpanded: boolean;
  onToggle: () => void;
}

const ResourceRequirementsSection: React.FC<ResourceRequirementsSectionProps> = ({ 
  resourceRequirements, 
  isExpanded, 
  onToggle 
}) => {
  const { t } = useLanguage();

  return (
    <div className="enhancement-section">
      <button className="section-header" onClick={onToggle} type="button">
        <div className="section-title-group">
          <span className="section-icon">🛠️</span>
          <h4>{t('businessKit.resources.title')}</h4>
        </div>
        <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`} />
      </button>

      {isExpanded && (
        <div className="section-content">
          {/* Skills Required */}
          {resourceRequirements.skills && resourceRequirements.skills.length > 0 && (
            <div className="skills-section">
              <h5>{t('businessKit.resources.skillsRequired')}</h5>
              <div className="skills-grid">
                {resourceRequirements.skills.map((skill, index) => (
                  <div key={index} className={`skill-card level-${skill.level}`}>
                    <div className="skill-header">
                      <span className="skill-name">{skill.skill}</span>
                      <span className={`skill-level ${skill.level}`}>
                        {t(`businessKit.resources.level.${skill.level}`)}
                      </span>
                    </div>
                    {skill.canLearn && (
                      <span className="skill-badge">
                        <i className="bi bi-book" />
                        {t('businessKit.resources.canLearn')}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tools Required */}
          {resourceRequirements.tools && resourceRequirements.tools.length > 0 && (
            <div className="tools-section">
              <h5>{t('businessKit.resources.toolsRequired')}</h5>
              <div className="tools-list">
                {resourceRequirements.tools.map((tool, index) => (
                  <div key={index} className="tool-card">
                    <div className="tool-header">
                      <span className="tool-name">{tool.name}</span>
                      <span className="tool-cost">{tool.cost}</span>
                    </div>
                    <p className="tool-purpose">{tool.purpose}</p>
                    {tool.alternatives && tool.alternatives.length > 0 && (
                      <div className="tool-alternatives">
                        <span className="alternatives-label">{t('businessKit.resources.alternatives')}</span>
                        <div className="alternatives-tags">
                          {tool.alternatives.map((alt, i) => (
                            <span key={i} className="alternative-tag">{alt}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Cost Estimate */}
          {resourceRequirements.estimatedCost && (
            <div className="cost-estimate-section">
              <h5>{t('businessKit.resources.estimatedCost')}</h5>
              <div className="cost-summary">
                <div className="cost-range">
                  <div className="cost-item">
                    <span className="cost-label">{t('businessKit.resources.minimumCost')}</span>
                    <span className="cost-value minimum">{resourceRequirements.estimatedCost.minimum}</span>
                  </div>
                  <div className="cost-item">
                    <span className="cost-label">{t('businessKit.resources.recommendedCost')}</span>
                    <span className="cost-value recommended">{resourceRequirements.estimatedCost.recommended}</span>
                  </div>
                </div>
                {resourceRequirements.estimatedCost.breakdown && resourceRequirements.estimatedCost.breakdown.length > 0 && (
                  <div className="cost-breakdown">
                    <span className="breakdown-label">{t('businessKit.resources.breakdown')}</span>
                    <div className="breakdown-items">
                      {resourceRequirements.estimatedCost.breakdown.map((item, index) => (
                        <div key={index} className="breakdown-item">
                          <span className="breakdown-category">{item.category}</span>
                          <span className="breakdown-amount">{item.amount}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Launch Guide Component
interface LaunchGuideSectionProps {
  launchGuide: LaunchGuide;
  isExpanded: boolean;
  onToggle: () => void;
}

const LaunchGuideSection: React.FC<LaunchGuideSectionProps> = ({ launchGuide, isExpanded, onToggle }) => {
  const { t } = useLanguage();

  return (
    <div className="enhancement-section">
      <button className="section-header" onClick={onToggle} type="button">
        <div className="section-title-group">
          <span className="section-icon">🚀</span>
          <h4>{t('businessKit.launchGuide.title')}</h4>
        </div>
        <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`} />
      </button>

      {isExpanded && (
        <div className="section-content">
          {launchGuide.totalTimeline && (
            <div className="timeline-summary">
              <i className="bi bi-clock" />
              <span>{t('businessKit.launchGuide.totalTimeline')}: {launchGuide.totalTimeline}</span>
            </div>
          )}

          <div className="launch-phases">
            {launchGuide.phases.map((phase, index) => (
              <div key={index} className="phase-card">
                <div className="phase-header">
                  <div className="phase-number">{phase.phase}</div>
                  <div className="phase-info">
                    <h6 className="phase-name">{phase.name}</h6>
                    <span className="phase-duration">{phase.duration}</span>
                  </div>
                </div>

                {phase.milestones && phase.milestones.length > 0 && (
                  <div className="phase-milestones">
                    <span className="milestones-label">
                      <i className="bi bi-flag" />
                      {t('businessKit.launchGuide.milestones')}
                    </span>
                    <ul>
                      {phase.milestones.map((milestone, i) => (
                        <li key={i}>{milestone}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {phase.actions && phase.actions.length > 0 && (
                  <div className="phase-actions">
                    <span className="actions-label">
                      <i className="bi bi-list-check" />
                      {t('businessKit.launchGuide.actions')}
                    </span>
                    <ul>
                      {phase.actions.map((action, i) => (
                        <li key={i}>{action}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {index < launchGuide.phases.length - 1 && (
                  <div className="phase-connector">
                    <i className="bi bi-arrow-down" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessKitEnhancements;
